import React from 'react';
import {NeuroMessage, useProduct, useGuideConfig, useNeuroState} from "@ignitesales/neuro-application";
import classnames from "classnames";
import {useThemeSettings} from "../hooks/useThemeSettings.mjs";
import Collapsible from "react-collapsible";
import BasicProduct from "./BasicProduct";
import {useDialogueGuideSettings} from "../hooks/useDialogueGuideSettings.mjs";
import { ActionService } from '../services/ActionService.mjs';

const productComponentClass = 'product';

const DialogueProductWrapper = ({ className, product, ProductComponent,  onExternalProductClickHook, ...otherProductProps }) => {
    const { user } = useNeuroState();
    const neuroProductProps = useProduct(product);
    const {
        description,
        disclosures,
        features,
        name,
        buttonText,
        onProductActionClick,
        url,
        applyUrl,
        onSelectClick,
        isSelected
    } = neuroProductProps;
    const { customConfigs } = useGuideConfig();
    const { disableProductActions, disclosureDisplay } = useDialogueGuideSettings();
    const { getSummaryThemeConfigs } = useThemeSettings();
    const { shouldCollapseProductInfo } = getSummaryThemeConfigs();
    const shouldShowProductActions =  !disableProductActions;
    const componentClass = productComponentClass;
    const classes = classnames( componentClass, className );


    const onProductAction = async (actionAppkey, product) => {

        if (typeof onExternalProductClickHook === 'function') {
            await onExternalProductClickHook(actionAppkey, product);
        }

        onProductActionClick(actionAppkey, product);

        console.log('product button clicked', actionAppkey, product);
        const actions = await ActionService.getActionsByTriggerType('productAction');
        for(let action of actions){
            for(let trigger of action.triggers){
                if(trigger.type == 'productAction' && (!trigger.targetProductId || trigger.targetProductId == 'any' || trigger.targetProductId === product.id)){
                    ActionService.triggerAction(action, trigger?._id, await ActionService.extractActionParams(action, user, customConfigs?.guideActionServiceMapper)).then((e) => {
                        console.log('action ' + action.name + ' was triggered'); 
                    });
                }
            }
        }
    }

    const renderProductFeatures = () => {
        return (features.length) ? <div className={ `${ componentClass }__features` }>
            <NeuroMessage id="product.features.title" tagName="div" />
            <ol>{ features.map( (f,i) => <li key={i}>{f}</li>) }</ol>
        </div> : null;
    };

    const renderProductDisclosures = () => {
        const DisclosureList = disclosureDisplay === 'bullet' ? 'ul' : disclosureDisplay === 'number' ? 'ol' : 'ul';
        return (disclosures.length) ? <div className={ `${ componentClass }__disclosures` }>
            <NeuroMessage id="product.disclosures.title" tagName="div" />
            <DisclosureList>{ disclosures.map( (f,i) => <li key={i}>{f}</li>) }</DisclosureList>
        </div> : null;
    };

    const _collapsibleProductInfoTitle = isOpen => <div className='productCollapseTitle'>
        <NeuroMessage id={`product.collapsible.${isOpen ? 'open' : 'closed'}.title`} tagName="span" />
        <i className={ isOpen ? 'fa fa-minus-circle' : 'fa fa-plus-circle'} />
    </div>;

    const productFeaturesAndDisclosuresRender = shouldCollapseProductInfo && (features.length || disclosures.length)
        ?
        <Collapsible
            contentInnerClassName="productCollapseSection"
            trigger={_collapsibleProductInfoTitle(false)}
            triggerWhenOpen={_collapsibleProductInfoTitle(true)}
            transitionTime={250}
        >
            { renderProductFeatures() }
            { renderProductDisclosures() }
        </Collapsible>
        :
        <div>{ renderProductFeatures() }{ renderProductDisclosures() }</div>;

    const productActions = shouldShowProductActions && <div className={`${componentClass}__actions`}>
        <a href={applyUrl} target="_blank" onClick={onProductAction} className="btn btn-secondary product-action-button">
            { buttonText || 'Apply' }
        </a>
    </div>;

    const cartButton =  <div className={`${componentClass}__cartButton`}>
        <button onClick={onSelectClick} className={`cart-action-button ${isSelected ? 'cart-action-button--remove' : 'cart-action-button--add'}`}>
            <NeuroMessage id={isSelected ? 'cart.remove' : 'cart.add'} />
        </button>
    </div>;

    const dialogueProductProps = {
        renderProductDisclosuresFn: renderProductDisclosures,
        renderProductFeaturesFn: renderProductFeatures,
        productFeaturesAndDisclosuresRender: productFeaturesAndDisclosuresRender,
        productActionsRender: productActions,
        description,
        name,
        productClassNames: classes,
        productUrl: url,
        cartButtonRender: cartButton,
        onProductButtonClick: onProductAction,
        ...otherProductProps
    }

    const Product = ProductComponent ? ProductComponent : BasicProduct ;

    return <Product product={product} neuroProductProps={neuroProductProps} {...dialogueProductProps} />;
};

export default DialogueProductWrapper;
export {
    productComponentClass,
    DialogueProductWrapper
}
